import { useSearchParams } from "react-router-dom";
import { useCallback } from "react";

import WeekViewItem from "./WeekViewItem/WeekViewItem";
import {
    DayMapping,
    DayOfWeek,
    IWeeklySchedule,
    IWeeklyScheduleDashboard,
} from "../../../../schedule/types/schedule.types";
import moment from "../../../../../utils/moment";
import ListState from "../../../../../components/ListState/ListState";
import EmptyWeekView from "./EmptyWeekView";
import useGetWeekViewData from "../../../hooks/useGetWeekViewData";
import { formatDate } from "../../../../../utils/helpers";
import Loader from "../../../../../shared-ui/Loader/Loader";

export default function MyWeekView() {
    const [searchParams] = useSearchParams();

    const dayTitleClasses =
        "bg-[--card-bg-dark] py-8 text-caption uppercase font-medium text-textSec text-center";
    const dayItemsWrapperClass = `min-w-[150px] flex flex-col [border-right:1px_solid_var(--color-stroke)]`;
    const dayItemWrapperClass = `h-[184px] p-8 w-full [border-top:1px_solid_var(--color-stroke)]`;

    const startOfWeekFallback = moment()
        .startOf("isoWeek")
        .clone()
        .startOf("isoWeek")
        .format("YYYY-MM-DD");

    const startOfWeek = searchParams.get("from_date") || startOfWeekFallback;

    const {
        data: weekData,
        loading: weekLoading,
        error: weekError,
        isSuccess: weekIsSuccess,
        isWithinInterval,
    } = useGetWeekViewData();

    const allSchedulesByDay: { [key: string]: IWeeklyScheduleDashboard[] } = {
        sun: [],
        mon: [],
        tue: [],
        wed: [],
        thu: [],
        fri: [],
        sat: [],
    };
    const startOfWeekMoment = moment(startOfWeek, "YYYY-MM-DD");

    const firstDay =
        startOfWeekMoment.day() === 0
            ? startOfWeekMoment.clone().add(1, "day")
            : startOfWeekMoment.clone().startOf("isoWeek");

    const daysOfWeekFormatted = Array.from({ length: 7 }).map((_, index) =>
        firstDay.clone().add(index, "days").format("D ddd")
    );

    // Group schedules by day of the week based on session_start_date
    const seenSchedules = new Set();

    const dayOfWeekMap: DayMapping = {
        Sun: "sun",
        Mon: "mon",
        Tue: "tue",
        Wed: "wed",
        Thu: "thu",
        Fri: "fri",
        Sat: "sat",
    };

    if (isWithinInterval) {
        weekData?.data
            ?.filter(
                (item) =>
                    !item.is_cancelled &&
                    Boolean(item.confirmed_start_date_time)
            )
            .forEach((s) => {
                // Get the day of the week
                const scheduleDay = formatDate(
                    s.confirmed_start_date_time || "",
                    false,
                    "eee"
                );

                const dayOfWeek = dayOfWeekMap[scheduleDay as DayOfWeek];

                const schedule = {
                    ...s,
                    start: s.confirmed_start_date_time,
                    end: s.confirmed_end_date_time,
                    possible_start_date_time: s.possible_start_date_time,
                    possible_end_date_time: s.possible_end_date_time,
                    confirmed_start_date_time: s.confirmed_start_date_time,
                    confirmed_end_date_time: s.confirmed_end_date_time,
                };

                const scheduleKey = `${s.session_day}_${s.session_start_hr}_${
                    s.session_start_min
                }_${s.session_end_hr}_${s.session_end_min}_${
                    s.client.first_name
                }_${s.client.last_name}_${s.service}_${s.session_id}_${
                    s.session_history_id ?? "history_id"
                }`;

                if (!seenSchedules.has(scheduleKey)) {
                    allSchedulesByDay[dayOfWeek]?.push({
                        ...schedule,
                        cancelled_by: null,
                    });
                    seenSchedules.add(scheduleKey);
                }
            });
    } else {
        weekData?.data
            ?.filter((item) => !item.is_cancelled)
            .forEach((s) => {
                // Get the day of the week
                const scheduleDay = formatDate(
                    s.possible_start_date_time || "",
                    false,
                    "eee"
                );
                const dayOfWeek = dayOfWeekMap[scheduleDay as DayOfWeek];

                const schedule = {
                    ...s,
                    start: s.possible_start_date_time,
                    end: s.possible_end_date_time,
                    possible_start_date_time: s.possible_start_date_time,
                    possible_end_date_time: s.possible_end_date_time,
                    confirmed_start_date_time: s.confirmed_start_date_time,
                    confirmed_end_date_time: s.confirmed_end_date_time,
                };

                const scheduleKey = `${s.session_day}_${s.session_start_hr}_${s.session_start_min}_${s.session_end_hr}_${s.session_end_min}_${s.client.first_name}_${s.client.last_name}_${s.service}_${s.session_id}`;

                if (!seenSchedules.has(scheduleKey)) {
                    allSchedulesByDay[dayOfWeek]?.push({
                        ...schedule,
                        cancelled_by: null,
                    });
                    seenSchedules.add(scheduleKey);
                }
            });
    }

    const getDayOrder = (dayAbbreviation: string) => {
        const dayOrder = {
            Mon: 1,
            Tue: 2,
            Wed: 3,
            Thu: 4,
            Fri: 5,
            Sat: 6,
            Sun: 7,
        };
        return dayOrder[dayAbbreviation as keyof typeof dayOrder];
    };

    const timeToMinutes = (hour: number, minute: number) => {
        return hour * 60 + minute;
    };

    const sortSchedule = useCallback(
        (sessionList: (IWeeklySchedule | null)[]) => {
            return (
                sessionList.sort((a, b) => {
                    if (!a && !b) return 0;
                    if (!a) return 1;
                    if (!b) return -1;

                    const startDateTimeForA = isWithinInterval
                        ? a.confirmed_start_date_time
                        : a.possible_start_date_time;
                    const startDateTimeForB = isWithinInterval
                        ? b.confirmed_start_date_time
                        : b.possible_start_date_time;

                    const dayOrderA = getDayOrder(
                        startDateTimeForA
                            ? formatDate(startDateTimeForA, false, "EEE")
                            : a.session_day
                    );
                    const dayOrderB = getDayOrder(
                        startDateTimeForB
                            ? formatDate(startDateTimeForB, false, "EEE")
                            : b.session_day
                    );

                    if (dayOrderA == null && dayOrderB == null) return 0;
                    if (dayOrderA == null) return 1; // Invalid day goes to end
                    if (dayOrderB == null) return -1;

                    if (dayOrderA !== dayOrderB) {
                        return dayOrderA - dayOrderB;
                    }

                    const timeA = timeToMinutes(
                        startDateTimeForA
                            ? Number(formatDate(startDateTimeForA, false, "H"))
                            : a.session_start_hr,
                        startDateTimeForA
                            ? Number(formatDate(startDateTimeForA, false, "m"))
                            : a.session_start_min
                    );
                    const timeB = timeToMinutes(
                        startDateTimeForB
                            ? Number(formatDate(startDateTimeForB, false, "H"))
                            : b.session_start_hr,
                        startDateTimeForB
                            ? Number(formatDate(startDateTimeForB, false, "m"))
                            : b.session_start_min
                    );

                    if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
                    if (Number.isNaN(timeA)) return 1; // Invalid time goes to end
                    if (Number.isNaN(timeB)) return -1;

                    return timeA - timeB;
                }) || []
            );
        },
        [isWithinInterval]
    );

    const padArrayToLength = <T,>(
        arr: T[],
        length: number,
        fillValue = null
    ): (T | null)[] => {
        return arr.length >= length
            ? arr.slice(0, length)
            : [...arr, ...Array(length - arr.length).fill(fillValue)];
    };

    const longestArray = Object.values(allSchedulesByDay).reduce((a, b) =>
        a.length > b.length ? a : b
    );
    const maxItems = longestArray.length;

    const paddedSchedulesByDay = Object.fromEntries(
        Object.entries(allSchedulesByDay).map(([day, schedules]) => [
            day,
            padArrayToLength(schedules, maxItems),
        ])
    ) as { [key: string]: (IWeeklySchedule | null)[] };

    const weekViewCardsToDisplay = (day: string) => {
        return sortSchedule(paddedSchedulesByDay[day]).map((item, i) => {
            if (item) {
                return (
                    <div
                        key={item.session_id + 1}
                        className={dayItemWrapperClass}
                    >
                        <WeekViewItem schedule={item} />
                    </div>
                );
            }
            return (
                <EmptyWeekView
                    className={dayItemWrapperClass}
                    key={`${i + 1}`}
                />
            );
        });
    };

    return (
        <>
            {weekLoading ? <Loader /> : null}
            {weekError ? (
                <ListState
                    isError
                    errorMsg={
                        weekError?.response?.data?.message ||
                        "Could not load schedule at this time"
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                    context="general"
                />
            ) : null}
            {weekIsSuccess &&
            weekData &&
            weekData.data.filter(
                (item) =>
                    !item.is_cancelled &&
                    Boolean(item.confirmed_start_date_time)
            ).length === 0 ? (
                <ListState
                    stateHelperText="No schedule found at this time"
                    isError={false}
                    emptyMessage="No schedule found"
                    context="general"
                />
            ) : null}
            {weekIsSuccess &&
            weekData &&
            weekData.data.filter(
                (item) =>
                    !item.is_cancelled &&
                    Boolean(item.confirmed_start_date_time)
            ).length > 0 ? (
                <div
                    className="rounded-md [border:1px_solid_var(--color-stroke)]
                         grid grid-cols-[40px,repeat(7,minmax(150px,1fr))] w-full max-w-full overflow-x-auto"
                >
                    <div className="w-10 flex flex-col [border-right:1px_solid_var(--color-stroke)]">
                        <div className="bg-[--card-bg-dark] [border-bottom:1px_solid_var(--color-stroke)]  rounded-tl-md">
                            <span className="h-8 w-full block" />
                        </div>
                        <div className="flex-1" />
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[0]}
                        </div>
                        {weekViewCardsToDisplay("mon")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[1]}
                        </div>
                        {weekViewCardsToDisplay("tue")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[2]}
                        </div>
                        {weekViewCardsToDisplay("wed")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[3]}
                        </div>
                        {weekViewCardsToDisplay("thu")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[4]}
                        </div>
                        {weekViewCardsToDisplay("fri")}
                    </div>
                    <div className={dayItemsWrapperClass}>
                        <div className={dayTitleClasses}>
                            {daysOfWeekFormatted[5]}
                        </div>
                        {weekViewCardsToDisplay("sat")}
                    </div>
                    <div className={`${dayItemsWrapperClass} border-r-0`}>
                        <div className={`${dayTitleClasses} !border-none`}>
                            {daysOfWeekFormatted[6]}
                        </div>
                        {weekViewCardsToDisplay("sun")}
                    </div>
                </div>
            ) : null}
        </>
    );
}
